/**
 * AdminRoadmap — vista interna del roadmap, changelog y PRD.
 *
 * Lee `/app/memory/*.md` desde el backend (sólo admin) y los renderiza con
 * markdown. Útil para imprimir, revisar o copiar al Excel personal.
 */
import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Map as MapIcon, Download, Printer, FileText, History, RefreshCw, Loader2,
} from "lucide-react";

const TABS = [
  { key: "roadmap",   label: "Roadmap",   icon: MapIcon, file: "DNF_ROADMAP.md",   endpoint: "/admin/roadmap/download" },
  { key: "changelog", label: "Changelog", icon: History, file: "DNF_CHANGELOG.md", endpoint: "/admin/roadmap/changelog.md" },
  { key: "prd",       label: "PRD",       icon: FileText, file: "DNF_PRD.md",      endpoint: "/admin/roadmap/prd.md" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

const fmtBytes = (b) => {
  if (!b && b !== 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

const MD_COMPONENTS = {
  h1: (props) => <h1 className="text-2xl font-bold text-amber-300 mt-6 mb-3 border-b border-zinc-800 pb-2 print:text-amber-800" {...props} />,
  h2: (props) => <h2 className="text-xl font-bold text-amber-300 mt-5 mb-2 print:text-amber-800" {...props} />,
  h3: (props) => <h3 className="text-base font-bold text-amber-200 mt-4 mb-2 print:text-amber-700" {...props} />,
  h4: (props) => <h4 className="text-sm font-semibold text-amber-200 mt-3 mb-1 print:text-amber-700" {...props} />,
  p: (props) => <p className="mb-2 text-zinc-300 print:text-black" {...props} />,
  strong: (props) => <strong className="font-bold text-zinc-100 print:text-black" {...props} />,
  em: (props) => <em className="italic text-zinc-300 print:text-black" {...props} />,
  code: ({ inline, ...props }) =>
    inline
      ? <code className="bg-zinc-900 text-amber-300 px-1 py-0.5 rounded text-xs font-mono print:bg-zinc-100 print:text-zinc-800" {...props} />
      : <code className="block bg-zinc-900 text-amber-200 p-3 rounded text-xs font-mono whitespace-pre-wrap my-2 print:bg-zinc-100 print:text-zinc-900" {...props} />,
  ul: (props) => <ul className="list-disc ml-5 space-y-1 my-2 text-zinc-300 print:text-black" {...props} />,
  ol: (props) => <ol className="list-decimal ml-5 space-y-1 my-2 text-zinc-300 print:text-black" {...props} />,
  li: (props) => <li className="text-sm leading-relaxed" {...props} />,
  a: (props) => <a className="text-sky-300 hover:text-sky-200 underline" target="_blank" rel="noreferrer" {...props} />,
  hr: (props) => <hr className="my-4 border-zinc-800" {...props} />,
  blockquote: (props) => <blockquote className="border-l-4 border-amber-500/40 pl-3 py-1 my-2 text-zinc-400 italic print:text-zinc-600" {...props} />,
  table: (props) => <table className="w-full text-xs border border-zinc-800 my-3" {...props} />,
  th: (props) => <th className="border border-zinc-800 px-2 py-1 bg-zinc-900 text-left" {...props} />,
  td: (props) => <td className="border border-zinc-800 px-2 py-1" {...props} />,
};

export default function AdminRoadmap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("roadmap");

  const cargar = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/roadmap");
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando roadmap");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const descargar = async (endpoint, filename) => {
    try {
      const res = await api.get(endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Error descargando");
    }
  };

  const current = data?.[tab] || { content: "", updated_at: null, size_bytes: 0 };
  const currentMeta = TABS.find((t) => t.key === tab);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <PageHeader
        icon={MapIcon}
        title="Roadmap y documentación interna"
        subtitle="Roadmap · Changelog · PRD — generados automáticamente."
        actions={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={cargar} disabled={loading}
              data-testid="admin-roadmap-refresh"
              className="border-zinc-700">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refrescar
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.print()}
              data-testid="admin-roadmap-print"
              className="border-zinc-700">
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Imprimir
            </Button>
            <Button size="sm" onClick={() => descargar(currentMeta.endpoint, currentMeta.file)}
              data-testid="admin-roadmap-download"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Descargar {currentMeta.label}
            </Button>
          </div>
        }
      />

      <div className="flex items-center gap-1 border-b border-zinc-900">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-roadmap-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors
                       ${tab === t.key
                         ? "border-amber-400 text-amber-200"
                         : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-4 text-[10px] text-zinc-500 font-mono uppercase tracking-wider">
        <span>{fmtBytes(current.size_bytes)}</span>
        <span>·</span>
        <span>Actualizado: {fmtDate(current.updated_at)}</span>
      </div>

      <div
        className="bg-zinc-950 border border-zinc-900 rounded-lg p-6 print:bg-white print:text-black overflow-x-auto"
        data-testid="admin-roadmap-content"
      >
        {loading ? (
          <div className="flex items-center justify-center py-12 text-zinc-400">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Cargando…
          </div>
        ) : !current.content ? (
          <div className="text-center text-zinc-600 italic py-12">Archivo vacío o no encontrado.</div>
        ) : (
          <div className="text-zinc-200 leading-relaxed text-sm print:text-black"
               data-testid="admin-roadmap-markdown">
            <ReactMarkdown components={MD_COMPONENTS}>
              {current.content}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}
